import React, { createContext, useContext, useRef, useCallback } from 'react';

export type AdSignupData = {
  companyName: string;
  contactName: string;
  email: string;
  password: string;
  phone: string;
};

type AdSignupContextType = {
  setSignupData: (data: AdSignupData) => void;
  getSignupData: () => AdSignupData | null;
  clearSignupData: () => void;
};

const AdSignupContext = createContext<AdSignupContextType | null>(null);

/**
 * Holds ad signup data (including password) in memory only — never persisted to
 * sessionStorage / localStorage. Mount this above both AdLandingPage and
 * AdQualificationStep (e.g. wrap both in a shared layout route).
 */
export const AdSignupProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dataRef = useRef<AdSignupData | null>(null);

  const setSignupData = useCallback((data: AdSignupData) => {
    dataRef.current = data;
  }, []);

  const getSignupData = useCallback(() => dataRef.current, []);

  const clearSignupData = useCallback(() => {
    dataRef.current = null;
  }, []);

  return (
    <AdSignupContext.Provider value={{ setSignupData, getSignupData, clearSignupData }}>
      {children}
    </AdSignupContext.Provider>
  );
};

export const useAdSignup = (): AdSignupContextType => {
  const ctx = useContext(AdSignupContext);
  if (!ctx) {
    throw new Error('useAdSignup must be used within an AdSignupProvider');
  }
  return ctx;
};
